import AxiosProvider from '@/app/utils/api/axios-provider';
import { postSseRequest, readSseStream } from '@/app/utils/api/sse-stream';
import { AxiosResponse } from 'axios';
import {
  BatchMetadataUploadResponse,
  BatchUploadRequest,
} from '@/app/shared/types/models/batch-metadata.interface';

export interface BatchMetadataProgressEvent {
  step: string;
  current: number;
  total: number;
  message: string;
}

interface BatchMetadataCompleteEvent extends BatchMetadataProgressEvent {
  metadataS3Key: string;
  deploymentS3Key: string;
  bucket: string;
  systemName: string;
  deploymentFileName: string;
}

function buildUploadFormData(request: BatchUploadRequest): FormData {
  const formData = new FormData();
  formData.append('deploymentFile', request.deploymentFile);
  formData.append(
    'metadata',
    new Blob([JSON.stringify(request.metadata)], { type: 'application/json' })
  );
  return formData;
}

export default {
  uploadMetadata(request: BatchUploadRequest): Promise<AxiosResponse> {
    return AxiosProvider.adminApiClient.post(
      '/batch/metadata/upload',
      buildUploadFormData(request),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
  /**
   * Upload batch metadata to S3, streaming progress events back from the
   * server while the work runs. Resolves with the final `complete` event
   * payload (which includes the same response data as the synchronous
   * endpoint); rejects if the server emits an `error` event or the request
   * itself fails.
   */
  async streamUploadMetadata(
    request: BatchUploadRequest,
    onProgress?: (_event: BatchMetadataProgressEvent) => void
  ): Promise<BatchMetadataUploadResponse> {
    const baseURL = (config.VUE_APP_ATB_ADMIN_SERVICE_URL || '').replace(
      /[\\/]+$/,
      ''
    );
    const url = `${baseURL}/batch/metadata/upload/stream`;

    const response = await postSseRequest(
      url,
      buildUploadFormData(request),
      'Batch upload failed'
    );

    const completeEvent = await readSseStream<
      BatchMetadataProgressEvent,
      BatchMetadataCompleteEvent
    >(response, onProgress, 'Batch upload failed');

    if (!completeEvent) {
      throw new Error('Batch upload stream ended without completion event');
    }

    return {
      s3Key: completeEvent.metadataS3Key,
      bucket: completeEvent.bucket,
      systemName: completeEvent.systemName,
      deploymentFileName: completeEvent.deploymentFileName,
    };
  },
  getMetadataHistory(): Promise<AxiosResponse> {
    return AxiosProvider.adminApiClient.get('/batch/metadata/history');
  },
  getInputSources(): Promise<AxiosResponse> {
    return AxiosProvider.adminApiClient.get('/batch/metadata/input-sources');
  },
  getInputManifest(): Promise<AxiosResponse> {
    return AxiosProvider.adminApiClient.get('/batch/metadata/input-manifest');
  },
};
